import * as React from "react";

import "./create-details-table.scss";

import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {Load}             from "@Common/react/load";
import {Load2}            from "@Common/react/load2";
import {UseState}         from "@Common/react/use-state";
import {Responsive}       from "@Common/ui-components/responsive";
import {SearchableSelect} from "@Common/ui-components/searchable-select/searchable-select";
import {sortMulti}        from "@Common/utils/collections";
import {chain}            from "@Common/utils/fs";
import {equalDeep}        from "@Common/utils/objects";

import {COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";

import {BranchSelectDropdown} from "../../branch-select-dropdown/branch-select-dropdown";

export const CreateDetailsTable = ({filter, next}) => cs(
    consumeContext("apis"),
    consumeContext("userLevels"),
    consumeContext("session"),

    ["agentSelectedState", ({}, next) => UseState({next})],
    ["programSelectedState", ({}, next) => UseState({next})],

    ["pList", ({apis}, next) => Load2({
        fetch: () => apis.next.program.getList(),
        next,
    })],
    ["programsList", ({pList}, next) => next([
        ...DEFAULT_PROGRAM,
        ...pList.value
            ?.map(({name, idProgram, idProgramType}) => ({idProgram, name: name || idProgram, idProgramType}))
            .filter((p) => p.idProgramType != XPRESS_CONNECT_ID)
        ?? [],
    ])],
    ["selectedVouchers", ({apis, agentSelectedState}, next) => Load2({
        _key: JSON.stringify([filter.value?.idBranch, agentSelectedState.value?.idAgent]),
        fetch: () => apis.next.vouchers.getVouchers1({
            search: agentSelectedState.value?.idAgent,
            idBranch: filter.value?.idBranch,
        }),
        next,
    })],
    ["availableProgramsList", ({programsList, selectedVouchers}, next) => next(filterUnusedPrograms(programsList, selectedVouchers.value))],
    ["isInternalUser", ({userLevels}, next) => next(userLevels?.internalUser)],
    ["agentList", ({apis, userLevels}, next) => {
        const idBranch = filter.value?.idBranch;
        return Load({
            _key: idBranch,
            fetch: () => (userLevels.userLevel === COMPANY_LEVEL && !idBranch) ? (
                null 
            ) : (
                idBranch === "ALL"
            ) ? (
                apis.next.getAgentList()
            ) : (
                apis.next.getAgentList(idBranch)
            ),
            next,
        });
    }],
    ["responsive", ({}, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>],
    ({userLevels, isInternalUser, session, agentList, agentSelectedState, availableProgramsList, programSelectedState, responsive}) => next({
        noProgramAvailable: availableProgramsList?.length === 0 && agentSelectedState.value?.idAgent,
        valid: programSelectedState.value?.idProgram != null,
        value: {
            idProgram: programSelectedState.value?.idProgram,
            idAgent: agentSelectedState.value?.idAgent,
            idRegion: agentSelectedState.value?.idRegion,
        },
        hasChanges: () => !equalDeep(INIT_VALUES, {idProgram: programSelectedState.value?.idProgram, idAgent: agentSelectedState.value?.idAgent}),
        render: () => (
            <div className="create-details-table-dd5">
                <div className="table">
                    {userLevels.userLevel === COMPANY_LEVEL && (
                        <div className="line">
                            {responsive.ge("md") && <div className="label" xd-qa-type="label" xd-qa-name="Office">
                                Office
                            </div>}
                            <div className="value">
                                <div className="line">
                                    {BranchSelectDropdown({
                                        filter,
                                        onChanged: () => {
                                            agentSelectedState?.onChange(null);
                                            programSelectedState?.onChange(null);
                                        },
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="line">
                        {responsive.ge("md") && <div className="label" xd-qa-type="label" xd-qa-name="User">
                            User
                        </div>}
                        <div className="value">
                            <div className="line">
                                {SearchableSelect({
                                    getSublabel: (agent) => (<>
                                        <div
                                            xd-qa-type="dropdown_item"
                                            xd-qa-name={`Office ${agent.idAgent}`}
                                        >
                                            {agent.agentLinkId} {isInternalUser && `| Internal ID: ${agent.idAgent}`}
                                        </div>
                                    </>),
                                    list: [
                                        ...chain(
                                            agentList ?? (session.data.agentNameFirst ? [{
                                                idAgent: session.value.agentId,
                                                agentNameFirst: session.data.agentNameFirst,
                                                agentNameLast: session.data.agentNameLast,
                                            }] : []),
                                            (_) => sortMulti(_, [
                                                (agent) => agent["agentNameLast"]?.toLowerCase(),
                                                (agent) => agent["agentNameFirst"]?.toLowerCase(),
                                            ])
                                        ),
                                    ],
                                    loading: !agentList,
                                    disabled: userLevels.userLevel === COMPANY_LEVEL && !filter.value?.idBranch,
                                    getLabel: (agent) => agent === "branch_order" ? (
                                        "Branch Order"
                                    ) : agent.agentLinkId ? (
                                        agent.agentNameLast + ", " + agent.agentNameFirst + ` (${agent.agentLinkId})`
                                    ) : (
                                        agent.agentNameLast + ", " + agent.agentNameFirst
                                    ),
                                    placeholder: "Please select a user",
                                    onSelect: async (agent) => {
                                        agentSelectedState.onChange(!agent ? {
                                        } : {
                                            idAgent: agent.idAgent,
                                            idRegion: agent.idRegion,
                                        });
                                        programSelectedState.onChange(null)
                                    },
                                    isSelected: (agent) => agentSelectedState.value?.idAgent === agent.idAgent,

                                    searchLabel: "Search a User",
                                    xdQaName: "User",
                                    scrollCloses: false,
                                })}
                            </div>
                            {chain(
                                [].filter(v=>v),
                                (_) => !_.length ? null : (
                                    <div className="line">
                                        {"(" + _.join(", ") + ")"}
                                    </div>
                                ),
                            )}
                        </div>
                    </div>

                    <div className="line">
                        {responsive.ge("md") && <div className="label" xd-qa-type="label" xd-qa-name="Program">
                            Program
                        </div>}
                        <div className="value">
                            {SearchableSelect({
                                ...isInternalUser && {
                                    getSublabel: (program) => program.idProgram && (`Internal ID: ${program.idProgram}`),
                                },
                                list: availableProgramsList,
                                loading: !availableProgramsList,
                                disabled: !agentSelectedState.value?.idAgent,
                                getLabel: (program) => program === "branch_order" ? (
                                    "Branch Order"
                                ) : program.name,
                                placeholder: "Please select a program",
                                onSelect: async (program) => {
                                    programSelectedState.onChange(!program ? {
                                    } : {
                                        idProgram: program.idProgram,
                                    });
                                },
                                isSelected: (program) => programSelectedState.value?.idProgram === program.idProgram,

                                searchLabel: "Search a Program",
                                xdQaName: "Program",
                                scrollCloses: false,
                            })}
                        </div>
                    </div>
                </div>
            </div>
        ),
    }),
);

const filterUnusedPrograms = (programs, vouchers) => !vouchers || vouchers.error ? null : (
    programs.filter((program) => !vouchers.some((voucher) => program.idProgram == voucher.idProgram))
);

const DEFAULT_PROGRAM = [
    {idProgram: "MAIN", name: "Main System"},
];
const INIT_VALUES = {
    idProgram: undefined,
    idAgent: undefined,
    idRegion: undefined,
}
const XPRESS_CONNECT_ID = 10;

